/**
 * Variant overrides for the v2 landing. Mirrors v1's variant keys so live ad URLs
 * keep working through the cutover. Variance is scoped to SEO + hero text + intent.
 * No featuresBar (dropped in v2). No per-variant photo (single hero photo for all).
 * CTA copy values are the baked-in winners from the v1 PostHog
 * landing-cta-variant-test (per-variant winning copy).
 */
export const landingV2Variants = {
  'car-finder': {
    seo: {
      title: 'Doocars: Find the right car for your life',
      description:
        "Not sure which car fits? Answer 20 questions and we'll find the right one for you.",
    },
    hero: {
      titleLine1: 'Not Sure Which Car?',
      titleLine2: 'Figure it out.',
      subtitle:
        'Answer 20 questions about how you drive and what you can spend.\nAI matches you to 5 cars that actually fit, then scores every UK listing daily.',
      cta: { title: 'Find my Car' },
    },
    onboardingIntent: 'car-finder',
  },
  'car-finder-gdn': {
    seo: {
      title: 'Doocars: Buying your first car?',
      description: "We'll find the right one for you.",
    },
    hero: {
      titleLine1: 'Buying Your First Car?',
      titleLine2: "We'll find the right one for you.",
      subtitle:
        "Tell us how you drive, where you live, what you can spend.\nWe match you to 5 cars and score every UK listing, so you don't have to guess.",
      cta: { title: 'Find my Car' },
    },
    onboardingIntent: 'car-finder',
  },
  deals: {
    seo: {
      title: 'Doocars: Car Hunting',
      description: 'Best matches in your inbox. Every morning.',
    },
    hero: {
      titleLine1: 'Let us Do the Car Hunting',
      titleLine2: 'Best cars. \nDaily.',
      subtitle:
        'Pick your models. We scan every UK car site, score each listing,\nand send you only the matches that beat the market.',
      cta: { title: 'Find Deals for Me' },
    },
    onboardingIntent: 'deals',
  },
  'price-check': {
    seo: {
      title: 'Doocars: Is that car actually worth it?',
      description: 'We check every listing against the UK market. Daily.',
    },
    hero: {
      titleLine1: 'Is It Worth It?',
      titleLine2: 'Best cars. \nDaily.',
      subtitle:
        'Every listing scored against the UK market on 10 factors:\nprice, mileage, days listed, history. You see the score before you go.',
      cta: { title: 'Score Cars for Me' },
    },
    onboardingIntent: 'deals',
  },
  'running-costs': {
    seo: {
      title: 'Doocars: What will it cost to run?',
      description: 'We score running costs on every UK listing. Daily.',
    },
    hero: {
      titleLine1: 'Is It Expensive To Run?',
      titleLine2: 'Best cars. \nDaily.',
      subtitle:
        'Fuel, road tax, insurance group, maintenance forecast.\nWe score the lifetime cost of every UK listing so you can compare like-for-like.',
      cta: { title: 'Score Running Costs for Me' },
    },
    onboardingIntent: 'deals',
  },
};

export function getContentForVariant(content, variantKey) {
  const variant = landingV2Variants[variantKey];
  if (!variant) return { content, variantLabel: 'default', onboardingIntent: undefined };
  const heroCta = { ...content.hero.cta, ...(variant.hero?.cta || {}) };
  // The variant's hero CTA copy is the single source of truth for every CTA on
  // the page (Header pill, Hero, Comparison footer, FinalCTA, mobile menu) so a
  // visitor on `?v=deals` sees "Find Deals for Me" everywhere, not just in the
  // hero.
  return {
    content: {
      ...content,
      seo: { ...content.seo, ...(variant.seo || {}) },
      hero: { ...content.hero, ...(variant.hero || {}), cta: heroCta },
      header: { ...content.header, cta: { ...content.header.cta, title: heroCta.title } },
      finalCTA: { ...content.finalCTA, cta: { ...content.finalCTA.cta, title: heroCta.title } },
    },
    variantLabel: variantKey,
    onboardingIntent: variant.onboardingIntent,
  };
}

export default landingV2Variants;
